import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, X, Trash2, Pencil, Star } from 'lucide-react';

const empty = {
  customer_name: '',
  rating: 5,
  text: '',
  date: '',
  featured: true,
  source: 'google',
  avatar_url: '',
  sort_order: 0,
};

const SOURCE_OPTIONS = [
  { value: 'google', label: 'Google' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'other', label: 'Other' },
];

const sourceLabel = (v) => (SOURCE_OPTIONS.find((s) => s.value === v)?.label) || 'Other';

const Stars = ({ value, onChange }) => (
  <div className="inline-flex gap-1">
    {[1,2,3,4,5].map((n) => (
      <button key={n} type="button" onClick={() => onChange && onChange(n)} aria-label={`${n} star${n>1?'s':''}`} className={`h-7 w-7 grid place-items-center rounded transition-colors ${n <= value ? 'text-[hsl(var(--sb-gold))]' : 'text-white/25'} ${onChange ? 'hover:text-[hsl(var(--sb-gold))]' : ''}`}>
        <Star size={18} fill={n <= value ? 'currentColor' : 'none'} />
      </button>
    ))}
  </div>
);

export default function AdminReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get('/admin/reviews');
    setReviews(data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...editing, rating: parseInt(editing.rating, 10) || 5, sort_order: parseInt(editing.sort_order, 10) || 0 };
    try {
      if (editing.id) {
        await api.put(`/admin/reviews/${editing.id}`, payload);
        toast.success('Review updated');
      } else {
        await api.post('/admin/reviews', payload);
        toast.success('Review created');
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error('Save failed'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete review from ${r.customer_name}?`)) return;
    try { await api.delete(`/admin/reviews/${r.id}`); toast.success('Review deleted'); await load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-reviews-page">
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-display text-3xl text-white">REVIEWS</h1>
          <button onClick={() => setEditing({ ...empty, date: new Date().toISOString().slice(0,10) })} className="h-10 px-4 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold inline-flex items-center gap-2"><Plus size={16} />New review</button>
        </div>
        <p className="text-sm text-white/65 mb-5">Manage the customer reviews shown on the homepage. Real reviews should be copied manually from the Smithy Browns Google profile.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.map((r) => (
            <div key={r.id} className="sb-card p-4 flex flex-col">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg text-white">{r.customer_name}</div>
                  <Stars value={r.rating} />
                  <div className="text-[11px] text-white/55 mt-1">{r.date} · {sourceLabel(r.source)}{r.featured ? ' · featured' : ''}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing({ ...r })} className="h-8 w-8 grid place-items-center rounded-md hover:bg-white/5 text-white/80"><Pencil size={14} /></button>
                  <button onClick={() => remove(r)} className="h-8 w-8 grid place-items-center rounded-md hover:bg-white/5 text-[hsl(var(--sb-red))]"><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-sm text-white/80 mt-3 line-clamp-4">{r.text}</p>
            </div>
          ))}
          {!reviews.length && <div className="col-span-full text-center text-white/55 py-10">No reviews yet.</div>}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" onClick={() => setEditing(null)}>
          <div className="w-full max-w-lg bg-[hsl(var(--sb-surface))] h-full overflow-y-auto border-l border-[hsl(var(--sb-border))]" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[hsl(var(--sb-border))] flex items-center justify-between">
              <div className="font-display text-2xl text-white">{editing.id ? 'EDIT REVIEW' : 'NEW REVIEW'}</div>
              <button onClick={() => setEditing(null)} className="h-9 w-9 grid place-items-center text-white/70"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <F label="Customer name" value={editing.customer_name} onChange={(v) => setEditing({ ...editing, customer_name: v })} />
              <div>
                <div className="text-[11px] uppercase tracking-widest text-white/55 mb-1">Rating</div>
                <Stars value={editing.rating} onChange={(n) => setEditing({ ...editing, rating: n })} />
              </div>
              <F label="Review text" textarea value={editing.text} onChange={(v) => setEditing({ ...editing, text: v })} />
              <F label="Date" type="date" value={editing.date} onChange={(v) => setEditing({ ...editing, date: v })} />
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-white/55">Source</span>
                <select
                  value={editing.source || 'google'}
                  onChange={(e) => setEditing({ ...editing, source: e.target.value })}
                  data-testid="review-source-select"
                  className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white"
                >
                  {SOURCE_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </label>
              <F label="Sort order" type="number" value={editing.sort_order} onChange={(v) => setEditing({ ...editing, sort_order: v })} />
              <label className="inline-flex items-center gap-2 text-sm text-white/85">
                <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured on homepage
              </label>
              <div className="flex gap-3 pt-2">
                <button onClick={save} className="flex-1 h-11 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold">{editing.id ? 'Save' : 'Create'}</button>
                <button onClick={() => setEditing(null)} className="h-11 px-4 rounded-md border border-[hsl(var(--sb-border))] text-white">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const F = ({ label, value, onChange, type = 'text', textarea, placeholder }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-white/55">{label}</span>
    {textarea ? (
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={4} placeholder={placeholder} className="mt-1 w-full p-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white placeholder:text-white/40" />
    ) : (
      <input type={type} value={value === null || value === undefined ? '' : value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white placeholder:text-white/40" />
    )}
  </label>
);
